import { showFormDialog } from '../showFormDialog';
/**
 * Opens a form dialog allowing the user to edit a widget’s configuration across multiple tabbed sections.
 * Tabs can include default sections (e.g., General, Validate) and optionally a Rules tab if rules exist in the root params.
 *
 * Each tab can define its own `onLoad` and `onSubmit` logic, allowing pre-processing and post-processing of form data.
 *
 * @param {WidgetComponentProps<any, any>} props - The widget's full component props, including context, config, and root params.
 * @param {(config?: unknown) => void} endEditing - A callback to finish editing. Called with the updated config or nothing if cancelled.
 * @param {{ title: string }} params - Dialog-specific parameters, including the title shown at the top.
 * @param {string} params.title - The title of the widget edit dialog.
 * @param {...EditorTab} tabs - One or more tab definitions representing sections of the edit form.
 */
export function showWidgetEditDialog(props, endEditing, params, ...tabs) {
    const rules = props.rootParams.rules;
    const rulesListValues = rules.map((rule) => ({ value: rule.type, label: rule.label }));
    if (rules && rules.length > 0) {
        tabs.push({
            label: 'Rules',
            onSubmit: (data) => {
                if (data.rules && data.rules.length === 0) {
                    delete data.rules;
                }
                return data;
            },
            children: [
                {
                    type: 'form.checkboxList',
                    key: 'rules',
                    values: rulesListValues,
                },
            ],
        });
    }
    showFormDialog({
        maxWidth: 'lg',
        title: params.title,
        config: {
            type: 'form',
            onLoad: (data) => {
                data = JSON.parse(JSON.stringify(data));
                for (const tab of tabs) {
                    if (tab.onLoad) {
                        data = tab.onLoad(data);
                    }
                }
                return data;
            },
            onSubmit: (data) => {
                for (const tab of tabs) {
                    if (tab.onSubmit) {
                        data = tab.onSubmit(data);
                    }
                }
                return data;
            },
            children: [
                {
                    type: 'form.tabs',
                    tabs: [
                        ...tabs.map((tab) => ({
                            label: tab.label,
                            children: tab.children,
                        })),
                    ],
                },
            ],
        },
        data: props.config,
        valueProviders: props.rootParams.valueProviders,
        subFormProviders: props.rootParams.subFormProviders,
        sx: {
            minHeight: 'min(calc(100vh - 264px), 600px)',
        },
        onSubmit: (data) => endEditing(data),
        onCancel: () => endEditing(),
    });
}
